import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, Plus, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import { useDisputes } from "../hooks/useDisputes";
import type { DisputeStatus } from "../types";
import { cn } from "@/shared/lib/cn";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

const statusBadgeClass: Record<DisputeStatus, string> = {
  inProgress: "bg-accent-soft text-accent-foreground",
  resolved: "bg-accent/15 text-accent",
  rejected: "bg-danger-soft text-danger",
  closed: "bg-card-muted text-muted-foreground",
};

export function AllDisputesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.disputes.list;
  const { exportTable } = usePageActions();
  const { disputes, loading, error } = useDisputes();

  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return disputes.filter((dispute) => {
      if (!search) return true;
      const haystack =
        `${dispute.displayId} ${dispute.hospital} ${dispute.company} ${dispute.reference} ${dispute.submittedDate} ${dispute.invoice}`.toLowerCase();
      return haystack.includes(search.toLowerCase());
    });
  }, [search, disputes]);

  const columns = [
    labels.columns.id,
    labels.columns.date,
    labels.columns.hospital,
    labels.columns.company,
    labels.columns.amount,
    labels.columns.status,
    labels.columns.actions,
  ];

  const handleExportCsv = () => {
    exportTable(
      "litiges.csv",
      columns.slice(0, -1),
      filtered.map((dispute) => [
        String(dispute.displayId),
        dispute.submittedDate,
        dispute.hospital,
        dispute.company,
        formatAmount(dispute.amount),
        labels.statuses[dispute.status],
      ]),
    );
  };

  const isEmptyList = !loading && disputes.length === 0;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[{ label: t.safecheck.nav.disputes }]}
        />
        <Link
          to="/dashboard/disputes/create"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.createButton}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-danger/20 bg-danger-soft px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {isEmptyList ? (
        <div className="rounded-xl border border-primary/20 bg-primary-soft px-4 py-3">
          <p className="text-sm text-primary">{labels.emptyRegistered}</p>
        </div>
      ) : (
        <div className="theme-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={labels.searchPlaceholder}
              className={`${inputClass} w-full min-w-[200px] max-w-sm`}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
              >
                <Download size={15} />
                {labels.exportCsv}
              </button>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
                aria-label={t.safecheck.reset}
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="safecheck-table w-full min-w-[960px]">
              <thead>
                <tr className="border-b border-border bg-card-muted/50">
                  {columns.map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center text-sm text-muted">
                      Chargement…
                    </td>
                  </tr>
                ) : (
                  <>
                    {filtered.map((dispute, index) => (
                      <tr
                        key={dispute.id}
                        className={cn(
                          "border-b border-border last:border-0 hover:bg-card-muted/30",
                          index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                        )}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {dispute.displayId}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{dispute.submittedDate}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{dispute.hospital}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{dispute.company}</td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {formatAmount(dispute.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                              statusBadgeClass[dispute.status],
                            )}
                          >
                            {labels.statuses[dispute.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/dashboard/disputes/${dispute.id}`}
                            aria-label={labels.actions.view}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:border-primary hover:bg-primary-soft"
                          >
                            <Eye size={16} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-16 text-center text-sm text-muted">
                          {labels.empty}
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
